import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import toast from 'react-hot-toast'

import {
  addItem as addItemThunk,
  updateItem as updateItemThunk,
  removeItem as removeItemThunk,
  loadList,
} from '../store/slices/watchlist.slice'

import { handleAddItem as utilHandleAddItem } from '../services/util.service'
import { watchlistAxios } from '../services/watchlist.service'

/* helpers */
const flattenPages = (bucket = {}) => Object.values(bucket).flat()
const EMPTY_OBJ = Object.freeze({})

export function useWatchlistActions(userId) {
  const dispatch = useDispatch()
  const loggedInUser = useSelector((s) => s.userModule.loggedInUser)

  /* Redux buckets */
  const filteredBuckets = useSelector(
    (s) => s.watchlist.watchlist[userId]?.filtered ?? EMPTY_OBJ,
    shallowEqual
  )
  const allBuckets = useSelector(
    (s) => s.watchlist.watchlist[userId]?.all ?? EMPTY_OBJ,
    shallowEqual
  )

  const filteredArr = useMemo(
    () => flattenPages(filteredBuckets),
    [filteredBuckets]
  )
  const allArr = useMemo(() => flattenPages(allBuckets), [allBuckets])

  const allReviews =
    useSelector((s) => s.reviewModule.reviews, shallowEqual) ?? []
  const animeMap =
    useSelector((s) => s.animeDB.relationsData, shallowEqual) ?? EMPTY_OBJ

  /* Local state */
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [statusToAdd, setStatusToAdd] = useState(null)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [reviewAnime, setReviewAnime] = useState(null)

  const [filterText, setFilterText] = useState('')
  const [filterStatus, setFilterStatus] = useState('')

  const canEdit =
    !!loggedInUser &&
    (loggedInUser._id === userId || loggedInUser.role === 'admin')

  useEffect(() => {
    if (!userId || !filterStatus) return
    dispatch(
      loadList({
        userId,
        listType: 'watchlist',
        cacheKey: 'filtered',
        options: {
          page: 1,
          limit: 10_000,
          status: filterStatus,
        },
      })
    )
  }, [dispatch, userId, filterStatus])

  /* Search + Add */
  const handleSearch = async (e) => {
    e?.preventDefault?.()
    if (!canEdit) return toast.error('אין לך הרשאה לערוך רשימה זו')
    if (!searchTerm.trim()) return

    try {
      const results = await watchlistAxios.searchAnime(searchTerm)
      if (!results.length) toast.error('לא נמצאו תוצאות')
      setSearchResults(results)
    } catch (err) {
      console.error(err)
      toast.error('שגיאה בחיפוש')
    }
  }

  const handleAddItem = (status, anime) => {
    utilHandleAddItem(
      status,
      anime,
      dispatch,
      loggedInUser,
      allArr,
      addItemThunk
    )
    setStatusToAdd(null)
    setSearchResults([])
    setSearchTerm('')
  }

  /* Increment / Decrement */
  const handleIncrement = (item) => {
    if (!canEdit) return toast.error('אין לך הרשאה!')
    const max = Number.isFinite(item.episodes) ? item.episodes : Infinity
    const nextPg = item.progress + 1
    if (nextPg > max) return

    const updates = {
      progress: nextPg,
      ...(nextPg === max && max !== Infinity ? { status: 'Completed' } : {}),
    }

    dispatch(
      updateItemThunk({
        userId,
        listType: 'watchlist',
        anizoneId: item.anizone_id,
        updates,
      })
    )

    if ([3, 8, max].includes(nextPg)) {
      const already = allReviews.some(
        (r) => +r.animeId === item.anizone_id && r.userId === loggedInUser._id
      )
      if (!already) {
        setReviewOpen(true)
        setReviewAnime(item)
      }
    }
  }

  const handleDecrement = (item) => {
    if (!canEdit) return toast.error('אין לך הרשאה!')
    if (item.progress === 0) return

    const nextPg = item.progress - 1
    const updates = { progress: nextPg }

    const eps = Number.isFinite(item.episodes) ? item.episodes : null
    if (item.status === 'Completed' && eps && nextPg < eps)
      updates.status = 'Watching'

    dispatch(
      updateItemThunk({
        userId,
        listType: 'watchlist',
        anizoneId: item.anizone_id,
        updates,
      })
    )
  }

  /* Remove / Save Changes */
  const handleRemoveItem = (anizoneId) => {
    if (!canEdit) return toast.error('אין לך הרשאה!')
    dispatch(removeItemThunk({ userId, listType: 'watchlist', anizoneId }))
  }

  const getUpdatedWatchItem = (item) => {
    const today = new Intl.DateTimeFormat('he-IL', {
      timeZone: 'Asia/Jerusalem',
    }).format(new Date())
    const completed = item.status === 'Completed'
    const eps = Number(item.episodes)
    const validEps = !Number.isNaN(eps) && eps > 0

    return {
      ...item,
      progress: completed && validEps ? eps : item.progress,
      start_date: item.start_date || null,
      finish_date: completed
        ? item.finish_date || today
        : item.finish_date || null,
    }
  }

  const handleSaveChanges = (editItem) => {
    if (!canEdit) return toast.error('אין לך הרשאה!')
    const updated = getUpdatedWatchItem(editItem)

    dispatch(
      updateItemThunk({
        userId,
        listType: 'watchlist',
        anizoneId: updated.anizone_id,
        updates: {
          progress: updated.progress,
          status: updated.status,
          start_date: updated.start_date,
          finish_date: updated.finish_date,
        },
      })
    )
  }

  /* Enriched arrays */
  const enrichedAll = useMemo(
    () => allArr.map((i) => ({ ...animeMap[i.anizone_id], ...i })),
    [allArr, animeMap]
  )
  const enrichedFiltered = useMemo(
    () => filteredArr.map((i) => ({ ...animeMap[i.anizone_id], ...i })),
    [filteredArr, animeMap]
  )

  /* Return API */
  return {
    loggedInUser,
    canEdit,
    enrichedAll,
    enrichedFiltered,

    filterText,
    setFilterText,
    filterStatus,
    setFilterStatus,

    searchTerm,
    setSearchTerm,
    searchResults,
    statusToAdd,
    setStatusToAdd,
    handleSearch,
    handleAddItem,

    handleIncrement,
    handleDecrement,
    handleRemoveItem,
    handleSaveChanges,
    getUpdatedWatchItem,

    reviewOpen,
    reviewAnime,
    setReviewOpen,
  }
}
